import { ACT_ITEM_TILE, ACT_ITEM_TILE_DEFAULTS, type ActItemTileParams } from "../../../src/game/act-item-tile.ts";
import type { MsgKey } from "./i18n";
import { isFountainSize } from "../../../src/game/fountain-size.ts";

/**
 * 玩法格（副玩法）配置。依据《玩法格管端配置表 V1.2》。
 *
 * **这里没有「给多少钱」字段**，这是整块配置最容易被误解的地方：
 * 每一步给多少（X）由预算引擎按当步基准给予额 g 反推，玩法格只回答两件事——
 * ① 我这个格子适合演多大的钱（ECONOMY：承载范围）；EVENT 不配置出现节奏；
 * ② 拿到 X 之后怎么演。
 *
 * 于是 ECONOMY 格必须**无缝覆盖** [0.3, 20.0]×g：漏一段就会出现
 * 「这一步该给这么多钱，但没有格子能演它」。发布前校验的就是这个并集。
 */
export type TileCat = "ECONOMY" | "EVENT";

/** reel_map：金额档位 → 转轮图案。图案只是金额的皮肤，落哪档演哪套，钱照发 X */
export interface ReelRow { gMin: number | null; gMax: number | null; comboId: string; art: string; label: string }
/**
 * 客户端认识的 combo_id（fly-game-app slot-777 reelPlan.ts 的 COMBO_BY_ID）。
 * 认不出的 id 客户端不报错、一律演最小档 cherry2，所以管端只给选这几个。
 * art / label 客户端没读，管端不再显示，存量值原样保留。
 */
export const REEL_COMBO_IDS = ["cherry2", "cherry3", "bar1", "bar2", "jackpot777"] as const;
/** size_map：金币喷泉按到手金额分大小（src/game/fountain-size.ts）。钱照发 X，只决定演哪座喷泉 */
export interface SizeRow { gMin: number | null; gMax: number | null; size: string }
/** 喷泉大小档的默认三档：新配或首次启用时用 */
export const FOUNTAIN_SIZE_DEFAULT: SizeRow[] = [
  { gMin: 1, gMax: 5, size: "small" },
  { gMin: 5, gMax: 10, size: "mid" },
  { gMin: 10, gMax: 20, size: "large" },
];
/** tier_map：钱不分档、动画分档 */
export interface TierRow { gMin: number | null; gMax: number | null; animLevel: string; desc: string }
/** display_schemes：命中/落空的组合与权重；命中箱金额之和恒等于 X */
export interface SchemeRow { id: string; hit: number | null; miss: number | null; weight: number | null }
/** 新方案档的 id：s<n> 里第一个没被占的。不按「行数 + 1」——删掉中间一行再加会和最后一行同号，保存一直被拒 */
export function nextSchemeId(rows: readonly SchemeRow[]): string {
  let n = rows.length + 1;
  while (rows.some((r) => r.id === `s${n}`)) n++;
  return `s${n}`;
}

export interface Tile {
  key: string;
  /** tile_type_id，前后端通讯用它，建立后不可改 */
  id: string;
  name: string;
  ja: string;
  cat: TileCat;
  /** 菜单与标题上的名字（i18n key） */
  label: MsgKey;
  skin: string;
  enabled: boolean;
  /** 最低客户端版本 "x.y.z"；空 = 不限。低于它的客户端抽不到引用本格的 spin 格 */
  minClient?: string | null;
  /** 所属副玩法组（A–E，可多选；2026-10-05）。spin 奖励行引用组，抽中后客户端在组内就近挑 */
  groups?: string[];
  /** ECONOMY：承载范围，当步基准给予额 g 的倍数 */
  carryMin?: number | null;
  carryMax?: number | null;
  /** 已废弃的出现节奏字段，仅兼容历史快照，不参与抽取或保存校验 */
  pity?: number | null;
  cooldown?: number | null;
  /** 演出总时长 ms */
  anim: number | null;
  skip: boolean;
  sort: number | null;
  /** extra_drop_channel_id，空 = 无 */
  drop: string;
  reel?: ReelRow[];
  tier?: TierRow[];
  /** 金币喷泉专有：按金额分大小 */
  size?: SizeRow[];
  /** 抢金库专有 */
  boxTotal?: number | null;
  jitter?: number | null;
  roundTo?: number | null;
  schemes?: SchemeRow[];
  /** 演出角色池 / 文案池 */
  npc?: string;
  text?: string;
  /** 活动道具格专有（2026-10-05）：基础值与空窗期回退，存在 params 上（src/game/act-item-tile.ts） */
  actItem?: ActItemTileParams | null;
  /** 规则说明（i18n key）——数值规则大多不在本页，得说清楚在哪 */
  note: MsgKey;
}

export const TILE_SEED: Tile[] = [
  {
    key: "coinrain", id: "coinrain", name: "金币喷泉", ja: "コインの噴水", cat: "ECONOMY", label: "nav.subgame-coinrain", skin: "skin_coinrain_default",
    enabled: true, carryMin: 1, carryMax: 20, anim: 3500, skip: true, sort: 2, drop: "",
    size: FOUNTAIN_SIZE_DEFAULT.map((r) => ({ ...r })),
    note: "tile.note.coinrain",
  },
  {
    key: "slot777", id: "slot777", name: "福引", ja: "福引スロット", cat: "ECONOMY", label: "tile.slot777", skin: "skin_slot_default",
    enabled: true, carryMin: 1, carryMax: 20, anim: 5000, skip: true, sort: 3, drop: "",
    reel: [
      { gMin: 1, gMax: 2, comboId: "cherry2", art: "art_cherry2.png", label: "樱桃×2" },
      { gMin: 2, gMax: 3, comboId: "cherry3", art: "art_cherry3.png", label: "樱桃×3" },
      { gMin: 3, gMax: 5, comboId: "bar1", art: "art_bar1.png", label: "BAR" },
      { gMin: 5, gMax: 8, comboId: "bar2", art: "art_bar2.png", label: "双BAR" },
      { gMin: 8, gMax: 20, comboId: "jackpot777", art: "art_777.png", label: "777大当り" },
    ],
    note: "tile.note.slot777",
  },
  {
    key: "jackpot", id: "jackpot", name: "大奖", ja: "ジャックポット", cat: "ECONOMY", label: "tile.jackpot", skin: "skin_jackpot_default",
    enabled: true, carryMin: 5, carryMax: 20, anim: 3500, skip: true, sort: 4, drop: "",
    tier: [
      { gMin: 5, gMax: 8, animLevel: "lv1", desc: "金币喷泉·小" },
      { gMin: 8, gMax: 12, animLevel: "lv2", desc: "金币喷泉·大 + 屏幕震动" },
      { gMin: 12, gMax: 20, animLevel: "lv3", desc: "全屏庆祝 + 神明祝福演出" },
    ],
    note: "tile.note.jackpot",
  },
  {
    key: "thunder", id: "thunder", name: "雷云", ja: "雷雲", cat: "EVENT", label: "tile.thunder", skin: "skin_thunder_default",
    enabled: true, pity: null, cooldown: null, anim: 6000, skip: false, sort: 5, drop: "",
    note: "tile.note.thunder",
  },
  {
    key: "visit", id: "visit", name: "抢金库", ja: "宝物庫あらし", cat: "EVENT", label: "tile.visit", skin: "skin_visit_default",
    enabled: true, pity: null, cooldown: null, anim: 8000, skip: false, sort: 6, drop: "",
    boxTotal: 5, jitter: 10, roundTo: 100, npc: "binbogami_a,binbogami_b", text: "text_visit_v1",
    schemes: [
      { id: "s1", hit: 4, miss: 0, weight: 30 },
      { id: "s2", hit: 3, miss: 1, weight: 40 },
      { id: "s3", hit: 2, miss: 2, weight: 30 },
    ],
    note: "tile.note.visit",
  },
  {
    key: "omamori", id: "omamori", name: "御守", ja: "御守り", cat: "EVENT", label: "tile.omamori", skin: "skin_omamori_default",
    enabled: true, pity: null, cooldown: null, anim: 2500, skip: true, sort: 7, drop: "",
    npc: "idol_gift", text: "text_omamori_v1",
    note: "tile.note.omamori",
  },
  /*
   * 领养点两格（产品文档 1.2 §4.1）。它们**不发钱**，只承担节奏：
   * pity / cooldown 是从这里读的（makeRhythmOf 只认掉落项里的 subgame 引用），
   * 真正发宠物的是同一条掉落项上的 `pet` 奖励，抽哪只由领养池决定。
   *
   * 两格的分工：收养馆 = 质（SR/UR 主要出处，20 次保底只走它）；
   * 草丛 = 量（N/R 为主偶有 SR，不吃保底也不推进计数）。
   * 出现按掉落权重抽取，不再使用旧版的保底和冷却。
   *
   * ⚠️ 设计稿里还没有这两格，已在 design-ref/exemptions.json 的 `_tiles` 登记豁免。
   */
  {
    key: "adopt_shop", id: "adopt_shop", name: "收养馆", ja: "里親カフェ", cat: "EVENT", label: "tile.adopt_shop", skin: "",
    enabled: false, pity: null, cooldown: null, anim: 2500, skip: true, sort: 8, drop: "",
    note: "tile.note.adopt_shop",
  },
  {
    key: "adopt_grass", id: "adopt_grass", name: "草丛", ja: "草むら", cat: "EVENT", label: "tile.adopt_grass", skin: "",
    enabled: false, pity: null, cooldown: null, anim: 2000, skip: true, sort: 9, drop: "",
    note: "tile.note.adopt_grass",
  },
  /*
   * 活动道具格（2026-10-05 leo：活动道具像领养点一样进副玩法组）。只经组到达：组行抽中后客户端可以就近挑它，
   * 领取时按当期活动发道具，空窗期按回退发金币 / 骰子。基础值与回退配在这一格上（src/game/act-item-tile.ts）。
   * 设计稿 tilepage.jsx 已同步加上这一格（2026-10-05）
   */
  {
    key: "act_item", id: "act_item", name: "Event Item", ja: "イベントアイテム", cat: "EVENT", label: "tile.act_item", skin: "",
    enabled: false, anim: 2000, skip: true, sort: 10, drop: "",
    note: "tile.note.act_item",
  },
];


/** 三级菜单项：一个玩法格一页 */
/**
 * 左侧菜单用的格子清单。**只取 key 与 label**——TILE_SEED 里的数值字段
 * 一概不进界面：那些是当初照竞品编的初值，显示出来跟真配置分不清。
 * 真数据一律来自 /admin/api/config/tiles。
 */
/**
 * 这一版还没配的格子，新建时用的**空壳**。
 *
 * 只带结构信息（属于哪类、演出协定、名字），数值一律留空——
 * TILE_SEED 里那些 carryMin/anim/pity 是当初照竞品编的初值，
 * 填进去看着就像已经配好了，策划分不清哪些是自己定的。
 */
/**
 * 玩法格显示名的**唯一解析口**：字典有就用字典，没有退回服务端配的名字。
 *
 * 名字有两个来源——前端 i18n（`nav.subgame-<id>`）与服务端 `cfg_tile.name_i18n`。
 * 菜单一直是「字典优先」，而总览页直接读 `tile.name`，于是同一个格子在菜单里
 * 叫「金币喷泉」、在总览里还是旧名（codex 复审发现）。改名只改一处必然分叉，
 * 所以解析收敛到这里，两边都调它。
 *
 * 优先字典而不是服务端：字典是本次发版说了算的中文名，服务端那份是运营在配置台
 * 填的（目前配置台还没有改玩法格名字的入口，所以它更新不及时）。等配置台能改名
 * 之后，正确的做法是**删掉字典里那条**、让服务端成为唯一来源——而不是继续两边维护。
 */
export function tileLabel(tile: Pick<Tile, "key" | "name" | "ja" | "label">, t: (k: Tile["label"]) => string): string {
  const s = t(tile.label);
  return s === tile.label ? (tile.name || tile.ja || tile.key) : s;
}

export function blankTile(key: string): Tile | null {
  const seed = TILE_SEED.find((t) => t.key === key);
  if (!seed) return null;
  return {
    key: seed.key, id: seed.id, name: seed.name, ja: seed.ja, cat: seed.cat,
    label: seed.label, skin: "", enabled: false,
    carryMin: null, carryMax: null,
    pity: null, cooldown: null,
    anim: null, skip: true, sort: seed.sort, drop: "",
    ...(seed.reel ? { reel: [] } : {}),
    ...(seed.tier ? { tier: [] } : {}),
    ...(seed.schemes ? { schemes: [] } : {}),
    // 活动道具格新建就带上默认参数（与 spin 表勾「活动道具」的预填同值），不带的话第一次保存必被拒
    ...(seed.key === ACT_ITEM_TILE ? { actItem: { ...ACT_ITEM_TILE_DEFAULTS } } : {}),
    note: seed.note,
  } as Tile;
}

// 活动道具格不单独出菜单（2026-10-06 leo：两个「活动道具」运营看懵了）：它的配置并在「活动道具」页（act-item）顶上
export const TILE_NAV = TILE_SEED.filter((t) => t.key !== ACT_ITEM_TILE).map((t) => ({ id: `subgame-${t.key}`, label: t.label }));

// 承载并集必须无缝覆盖的区间。下限从 0.3 提到 1.0 是跟着设计定版走的——
// 原来覆盖 0.3~1.5 的「普通金币格」已从玩法格里去掉，下限留在 0.3 就永远补不满。
export const COVER_LO = 1;
export const COVER_HI = 20;

/**
 * 发布校验：启用中的 ECONOMY 格承载区间并集，必须无缝覆盖 [0.3, 20]×g。
 * 返回第一个缺口，没有缺口返回 null——只说「不通过」没用，得说漏在哪一段。
 */
export function carryGap(tiles: Tile[]): { from: number; to: number } | null {
  const segs = tiles
    // isFinite：Infinity/NaN 一旦混进来，max(reach, Infinity) 会让覆盖检查永远通过
    .filter((t) => t.cat === "ECONOMY" && t.enabled && Number.isFinite(t.carryMin) && Number.isFinite(t.carryMax))
    .map((t) => [t.carryMin!, t.carryMax!] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  if (!segs.length) return { from: COVER_LO, to: COVER_HI };
  if (segs[0][0] > COVER_LO) return { from: COVER_LO, to: segs[0][0] };
  let reach = segs[0][1];
  for (const [a, b] of segs.slice(1)) {
    if (a > reach) return { from: reach, to: a };
    reach = Math.max(reach, b);
  }
  return reach < COVER_HI ? { from: reach, to: COVER_HI } : null;
}

/**
 * 档位表校验。三类问题分开报：
 * - badRows：端点缺失/非法（null、Infinity、min ≥ max）或与上一档不衔接的行
 * - coverGap：整表没盖住要求的 [lo, hi]（首档起点太高 / 末档终点太低）
 *
 * 旧版只比相邻边界相等：`[2,3][3,8][8,19]` 无缝但没盖住 [1,20] 也判通过，
 * 空值经 Number(null)===0 互等也判通过——两种都是「有金额没档位可演」。
 */
export function bandCheck(
  rows: { gMin: number | null; gMax: number | null }[],
  lo: number,
  hi: number,
): { badRows: number[]; coverGap: { from: number; to: number } | null; ok: boolean } {
  const badRows: number[] = [];
  rows.forEach((r, i) => {
    const bad =
      !Number.isFinite(r.gMin) || !Number.isFinite(r.gMax) || (r.gMin as number) >= (r.gMax as number) ||
      (i > 0 && Number.isFinite(rows[i - 1].gMax) && r.gMin !== rows[i - 1].gMax);
    if (bad) badRows.push(i);
  });
  let coverGap: { from: number; to: number } | null = null;
  if (!rows.length) coverGap = { from: lo, to: hi };
  else if (!badRows.length) {
    const first = rows[0].gMin as number;
    const last = rows[rows.length - 1].gMax as number;
    if (first > lo) coverGap = { from: lo, to: first };
    else if (last < hi) coverGap = { from: last, to: hi };
  }
  return { badRows, coverGap, ok: !badRows.length && !coverGap };
}

/** @deprecated 用 bandCheck；保留给旧调用点渐退 */
export const rowGaps = (rows: { gMin: number | null; gMax: number | null }[]) =>
  bandCheck(rows, -Infinity as number, Infinity as number).badRows;

/** reel / tier 各自要求覆盖的区间 */
/** size 要盖满 1～20：组行下金额取全部金币类副玩法的并集，挑中喷泉也可能拿到 20 倍 */
export const BAND_RANGE: Record<"reel" | "tier" | "size", [number, number]> = { reel: [1, 20], tier: [5, 20], size: [1, 20] };

/** scheme 行合法性 + Σweight；TilePage 用它决定保存能不能点 */
export function schemesCheck(tile: Tile): { ok: boolean } {
  const rows = tile.schemes ?? [];
  if (!rows.length) return { ok: true };
  const pick = (tile.boxTotal ?? 1) - 1;
  const sum = rows.reduce((a, r) => a + (r.weight ?? 0), 0);
  const rowsOk = rows.every((r) => (r.hit ?? 0) + (r.miss ?? 0) === pick && (r.hit ?? 0) >= 1);
  return { ok: sum === 100 && rowsOk };
}

/** 一个玩法格能不能保存：档位表 + 方案池 + 承载字段本身 */
/**
 * 保存被拦时，**缺的是哪一项**。返回 i18n key 列表，空数组 = 能存。
 *
 * 与 tileValid 同一套判断（它直接调这个），不另写一遍——两份判断迟早分叉成
 * 「按钮灰着但列不出问题」或者反过来，那比不提示更难查。
 *
 * **只列拦保存的项。** 发布才要求的（承载并集无缝覆盖、Σ1/pity 超限）不在这里：
 * 混进来会让星号的承诺失效——带星的都填完了、保存键还是按不动。
 * 那些归发布闸，页面上另有提示。
 */
export function tileMissing(t: Tile): string[] {
  const out: string[] = [];
  if (t.cat === "ECONOMY") {
    if (!Number.isFinite(t.carryMin)) out.push("tile.f.carryMin");
    if (!Number.isFinite(t.carryMax)) out.push("tile.f.carryMax");
    // 两个都填了才谈得上大小关系；缺哪个上面已经报过，别重复说
    if (Number.isFinite(t.carryMin) && Number.isFinite(t.carryMax)
      && (t.carryMin as number) >= (t.carryMax as number)) out.push("tile.err.carryOrder");
  }
  // 活动道具格没有出现节奏（只经组到达），要的是基础值与回退数量，上下界同服务端 act-item-tile.ts
  if (t.cat === "EVENT" && t.key === ACT_ITEM_TILE) {
    if (!Number.isInteger(t.actItem?.value) || t.actItem!.value < 1) out.push("tile.f.actValue");
    if (!Number.isInteger(t.actItem?.fallbackValue) || t.actItem!.fallbackValue < 1) out.push("tile.f.actFallback");
  }
  if (t.reel && !bandCheck(t.reel, ...BAND_RANGE.reel).ok) out.push("tile.err.reel");
  if (t.tier && !bandCheck(t.tier, ...BAND_RANGE.tier).ok) out.push("tile.err.tier");
  if (t.size && (!bandCheck(t.size, ...BAND_RANGE.size).ok || !t.size.every((r) => isFountainSize(r.size)))) out.push("tile.err.size");
  if (!schemesCheck(t).ok) out.push("tile.err.schemes");
  return out;
}

export function tileValid(t: Tile): boolean {
  return tileMissing(t).length === 0;
}
